import { DateTime } from 'luxon';
import { combineLatest, map, Observable, tap } from 'rxjs';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateRange, DateUtility } from '@jms/model';
import { WeekDatePipe } from '@jms/ui/ui.date.range.picker/src/week.date.pipe';
import { ObservableInput } from '@jms/utils/util.redux';
import { LuxonCalendarComponent } from '@workbuddy/ui/fields/luxon.calendar';

@Component({
  selector: 'appointments-date-range-picker',
  templateUrl: './date.range.component.html',
  styleUrls: ['./date.range.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WeekDatePipe],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LuxonCalendarComponent,
  ]
})
export class DateRangePickerComponent {

  @ObservableInput() @Input('startDate') public startDate$: Observable<Date>;
  @ObservableInput() @Input('endDate') public endDate$: Observable<Date>;
  @ObservableInput(Intl.DateTimeFormat().resolvedOptions().timeZone) @Input('timezone') public timezone$: Observable<string>;

  @Output() public moveNext = new EventEmitter<void>();
  @Output() public movePrev = new EventEmitter<void>();
  @Output() public goToToday = new EventEmitter<void>();
  @Output() public goToDate = new EventEmitter<Date>();

  public selectedDate = new FormControl();

  public range: DateRange;
  public styleIconCalendar: any;
  public timezone: string;

  public date$: Observable<string>;
  private navigatingToToday = false;

  constructor() {

    this.styleIconCalendar = {
      'background': 'transparent',
      'color': '#111',
      'font-size': '12px',
      'border': 'none'
    };

    this.selectedDate.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((date: DateTime) => {
          if (!this.navigatingToToday) {
            this.goToDate.emit(date?.toJSDate());
          }
        })
      ).subscribe();

    this.timezone$
      .pipe(
        takeUntilDestroyed(),
      )
      .subscribe(timezone => this.timezone = timezone);

    this.date$ = combineLatest([
      this.startDate$,
      this.endDate$,
      this.timezone$
    ])
      .pipe(
        map(([start, end, timezone]) => {

          if (!start || !end)
            return 'Invalid Dates';

          const startDate = DateUtility.getDateTime(start, timezone).startOf('day');
          const endDate = DateUtility.getDateTime(end, timezone)
            .minus({ milliseconds: 1 }) // a hack for FullCalendar to make sure we display the next day
            .endOf('day');

          const isRange = endDate.diff(startDate, 'hours').hours > 24;

          if (isRange) {

            const startParts = [startDate.toFormat('dd')];
            if (startDate.month != endDate.month)
              startParts.push(startDate.toFormat('MMM'));
            if (startDate.year != endDate.year)
              startParts.push(startDate.toFormat('yyyy'));

            const startPart = startParts.join(' ');
            const endPart = endDate.toFormat('dd MMM yyyy');

            return `${startPart} - ${endPart}`;

          }

          return DateUtility.formatDateTime(startDate, timezone, 'dd MMMM yyyy');

        })
      )

  }

  public today(): void {

    const today = DateTime.now().setZone(this.timezone);
    this.navigatingToToday = true;
    this.selectedDate.setValue(today);
    this.navigatingToToday = false;
    this.goToToday.emit();
  }

}
